import fs from "fs";
import path from "path";
import { CompatibilityIssue } from "./models.js";
import { PackagePlugin } from "../plugins/types.js";
import { DependencyAnalysis } from "./dependencyAnalyzer.js";

/**
 * Core compatibility engine.
 *
 * All rules are derived from either:
 *  - Loaded plugins (plugin.compatibilityRules())
 *  - Plugin analyzeRules()
 *  - Built-in structural checks (version alignment, peer deps)
 *
 * No hardcoded switch statements. No network calls.
 */
export class CompatibilityEngine {
  constructor(
    private projectRoot: string,
    private depAnalysis: DependencyAnalysis,
    private plugins: PackagePlugin[]
  ) {}

  async run(): Promise<CompatibilityIssue[]> {
    const issues: CompatibilityIssue[] = [];

    // 1. React Native version must be present
    if (this.depAnalysis.reactNativeVersion === "unknown") {
      issues.push({
        packageName: "react-native",
        severity: "error",
        message: "react-native not found in package.json dependencies.",
        recommendation: "Add react-native as a dependency: npm install react-native",
      });
    }

    // 2. React / React Native version alignment
    const { reactNativeVersion, reactVersion, typesReactNativeVersion, allDeps } =
      this.depAnalysis;

    if (reactVersion && reactNativeVersion !== "unknown") {
      const rnMinor = parseInt(reactNativeVersion.split(".")[1] ?? "0", 10);
      const reactMajor = parseInt(reactVersion.split(".")[0] ?? "0", 10);

      if (rnMinor >= 75 && reactMajor < 18) {
        issues.push({
          packageName: "react",
          severity: "warning",
          message: `React version (${reactVersion}) may be incompatible with React Native (${reactNativeVersion}).`,
          recommendation: "Upgrade React to version 18+.",
        });
      }
    }

    // 3. @types/react-native alignment
    if (typesReactNativeVersion && reactNativeVersion !== "unknown") {
      const rnParts = reactNativeVersion.split(".");
      const typesParts = typesReactNativeVersion.split(".");
      if (rnParts[0] !== typesParts[0] || rnParts[1] !== typesParts[1]) {
        issues.push({
          packageName: "@types/react-native",
          severity: "warning",
          message: `@types/react-native (${typesReactNativeVersion}) does not match react-native (${reactNativeVersion}).`,
          recommendation: `Update @types/react-native to match the react-native minor version.`,
        });
      }
    }

    // 4. Common peer dependency checks (plugin-agnostic structural rules)
    issues.push(...this.checkPeerDependencies(allDeps));

    // 5. Run plugin compatibility rules (each plugin supplies its own rules)
    for (const plugin of this.plugins) {
      if (plugin.compatibilityRules) {
        const rules = plugin.compatibilityRules();
        for (const rule of rules) {
          try {
            const triggered = await rule.check(this.projectRoot, allDeps);
            if (triggered) {
              issues.push({
                packageName: plugin.packageName,
                severity: "warning",
                message: rule.text || `Compatibility issue: ${rule.id}`,
                recommendation: undefined,
              });
            }
          } catch {
            // Ignore rule failures to avoid crashing the engine
          }
        }
      }
    }

    // 6. Run plugin analyzeRules() and convert Diagnostic → CompatibilityIssue
    for (const plugin of this.plugins) {
      if (plugin.analyzeRules) {
        try {
          const diagnostics = await plugin.analyzeRules(this.projectRoot);
          for (const diag of diagnostics) {
            issues.push({
              packageName: plugin.packageName,
              severity: diag.severity,
              message: diag.explanation || diag.title,
              recommendation: diag.fixes?.[0],
            });
          }
        } catch {
          // Ignore plugin failures
        }
      }
    }

    return issues;
  }

  private checkPeerDependencies(allDeps: Record<string, string>): CompatibilityIssue[] {
    const issues: CompatibilityIssue[] = [];

    // @react-navigation/native needs screens + safe-area-context
    if (allDeps["@react-navigation/native"]) {
      if (!allDeps["react-native-screens"]) {
        issues.push({
          packageName: "@react-navigation/native",
          severity: "warning",
          message:
            "Missing peer dependency 'react-native-screens' required by '@react-navigation/native'.",
          recommendation: "Run: npm install react-native-screens",
        });
      }
      if (!allDeps["react-native-safe-area-context"]) {
        issues.push({
          packageName: "@react-navigation/native",
          severity: "warning",
          message:
            "Missing peer dependency 'react-native-safe-area-context' required by '@react-navigation/native'.",
          recommendation: "Run: npm install react-native-safe-area-context",
        });
      }
    }

    // react-native-reanimated recommends gesture-handler
    if (allDeps["react-native-reanimated"] && !allDeps["react-native-gesture-handler"]) {
      issues.push({
        packageName: "react-native-reanimated",
        severity: "warning",
        message:
          "Missing recommended peer dependency 'react-native-gesture-handler' often used with 'react-native-reanimated'.",
        recommendation: "Run: npm install react-native-gesture-handler",
      });
    }

    // @react-native-firebase/app needs native setup reminder
    if (allDeps["@react-native-firebase/app"] && !allDeps["@react-native-firebase/messaging"]) {
      const hasGoogleServices = fs.existsSync(
        path.join(this.projectRoot, "android", "app", "google-services.json")
      );
      if (!hasGoogleServices) {
        issues.push({
          packageName: "@react-native-firebase/app",
          severity: "info",
          message:
            "Firebase detected but google-services.json not found in android/app/.",
          recommendation:
            "Download google-services.json from Firebase Console and place it in android/app/.",
        });
      }
    }

    return issues;
  }
}
